import React from 'react';
import { FaBalanceScale, FaFire, FaCheck } from "react-icons/fa";
import { FaTemperatureArrowUp, FaHouseFloodWater } from "react-icons/fa6";
import type { SiteAnalysisPanelProps } from '../../types';
import styles from './SiteAnalysisPanel.module.css';

const SiteAnalysisPanel: React.FC<SiteAnalysisPanelProps> = ({
  analysisData,
  position
}) => {
  const getTemperatureStatus = (temp: string, treePriority?: string) => {
    // Use treePriority for styling if available, otherwise default to normal
    if (treePriority === 'high') return 'critical';    // red (#DC2626)
    if (treePriority === 'medium') return 'high';      // orange (#F59E0B)  
    if (treePriority === 'low') return 'normal';       // green for low priority
    
    // Default to green for other cases
    return 'normal'; // Default = green (no tree_priority data)
  };

  const getEJPriorityStatus = (priority: string) => {
    const numPriority = parseFloat(priority.match(/\d+\.?\d*/)?.[0] || '0');
    // EJ Priority thresholds based on map layer styling (6.4-7.2 range)
    if (numPriority >= 7.0) return 'critical';  // 7.0+ = red (highest priority)
    if (numPriority >= 6.8) return 'high';      // 6.8-7.0 = orange (high priority)
    if (numPriority >= 6.4) return 'moderate';  // 6.4-6.8 = yellow (moderate priority)
    return 'normal';                            // < 6.4 = green (low priority)
  };

  const getFloodRiskStatus = (risk: string) => {
    // Flood zone status based on FEMA classifications
    if (risk.includes('High flood risk')) return 'critical';      // AE, A zones = red (high risk)
    if (risk.includes('High coastal')) return 'critical';         // VE, V zones = red (high coastal risk)
    if (risk.includes('Moderate flood risk')) return 'moderate';  // X zones = yellow (moderate risk)
    if (risk.includes('Undetermined')) return 'high';             // D zones = orange (unknown risk)
    return 'normal';                                              // No flood data = green (low risk)
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'moderate': return '#eab308';
      case 'normal':
      case 'low': return '#4ade80';
      default: return '#4ade80';
    }
  };

  // Check if we have any meaningful data to display
  const hasTemperatureData = analysisData.temperature;
  const hasEJData = analysisData.ejPriority;
  const hasFloodData = analysisData.floodRisk;
  
  // If no meaningful data exists, don't render the panel
  if (!hasTemperatureData && !hasEJData && !hasFloodData) {
    return null;
  }

  const temperatureStatus = hasTemperatureData ? getTemperatureStatus(analysisData.temperature!, analysisData.treePriority) : null;
  const ejStatus = analysisData.ejPriority ? getEJPriorityStatus(analysisData.ejPriority) : null;
  const floodStatus = analysisData.floodRisk ? getFloodRiskStatus(analysisData.floodRisk) : null;

  const containerStyle = position ? {
    position: 'fixed' as const,
    left: position.x,
    top: position.y,
    transform: 'translate(-50%, -100%)',
    marginTop: '-10px'
  } : {
    position: 'fixed' as const,
    bottom: '40px',
    right: '20px',
    transform: 'none',
    marginTop: '0'
  };

  return (
    <div 
      className={styles.floatingPanel}
      style={containerStyle}
      role="region"
      aria-label="Site analysis data"
    >
      <div className={styles.panelTitle}>Site Analysis</div>
      
      <div className={styles.infoGrid}>
        {hasTemperatureData && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>
              <span className={styles.statusIndicator} aria-hidden="true"><FaTemperatureArrowUp /></span>
              Temperature:
            </span>
            <span 
              className={styles.infoValue}
              style={{ color: getStatusColor(temperatureStatus!) }}
            >
              {analysisData.temperature}
            </span>
          </div>
        )}
        
        {analysisData.ejPriority && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>
              <span className={styles.statusIndicator} aria-hidden="true"><FaBalanceScale /></span>
              EJ Priority:
            </span>
            <span 
              className={styles.infoValue}
              style={{ color: getStatusColor(ejStatus!) }}
            >
              {analysisData.ejPriority}
            </span>
          </div>
        )}
        
        {analysisData.floodRisk && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>
              <span className={styles.statusIndicator} aria-hidden="true"><FaHouseFloodWater /></span>
              Flood Risk:
            </span>
            <span 
              className={styles.infoValue}
              style={{ color: getStatusColor(floodStatus!) }}
            >
              {analysisData.floodRisk}
            </span>
          </div>
        )}
        
      </div>
      
      <div className={styles.analysisSummary}>
        <div className={styles.summaryTitle}>Planting Recommendation</div>
        <div className={styles.summaryContent}>
          {temperatureStatus === 'critical' || temperatureStatus === 'high' ? (
            <span className={`${styles.recommendation} ${styles.priority}`}>
              <span className="flex items-center gap-1"><FaFire /> High priority for cooling trees</span>
            </span>
          ) : (
            <span className={`${styles.recommendation} ${styles.normal}`}>
              <span className="flex items-center gap-1"><FaCheck /> Suitable for general tree planting</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteAnalysisPanel;